import { RouteComponentProps } from "../../../../models/RouteComponent";
import useApiService from "../../../../hooks/useApiService";
import {
  SafeWorkMethodRecord,
  SiteJobTaskRecord,
  SiteSummeryRecord,
} from "../../../../models/DSF/SiteSummary";
import { CardActions, CardOverflow } from "@mui/joy";
import { Button } from "primereact/button";
import React, { Fragment, useState } from "react";
import { TabPanel, TabView } from "primereact/tabview";
import { CompanyDocumentRecord } from "../../../../models/DSF/CompanyDocuments";
import { Download, DownloadOff, Eye, ProgressActivity } from "../../../../assets/icons";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { getSafeWorkMethod } from "../../../../service/safeWorkMethodService";
import { Dialog } from "primereact/dialog";
import { StatusBadge } from "../../../../components/StatusBadge/StatusBadge";
import { formatDate } from "../../../../utils/DateUtils";
import { downloadFile } from "../../../../service/s3Service";

function SiteDocuments(props: RouteComponentProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [documents, setDocuments] = useState<CompanyDocumentRecord[]>([]);
  const [safeWorkMethod, setSafeWorkMethod] = useState<SafeWorkMethodRecord | null>(null);
  const [safeWorkMethodLoadingIndex, setSafeWorkMethodLoadingIndex] = useState<number | null>(null);

  const [downloadingArray, setDownloadingArray] = useState<string[]>([]);

  const { session } = props;
  const {
    data: siteSummary,
    loading: loadingSiteSummary,
    error: siteSummaryError,
  } = useApiService<SiteSummeryRecord[]>({
    method: "GET",
    url: "site-summary",
    params: {
      clientId: session.client_id,
      siteId: session.site_id,
    },
  });

  const {
    loading: loadingSiteDocuments,
    error: siteDocumentsError,
    fetchData: fetchDocuments,
  } = useApiService<CompanyDocumentRecord[]>({
    method: "GET",
    url: "site-documents",
    apiName: "open",
    fetchOnMount: false,
    onSuccess: data => {
      setDocuments(data);
    },
  });

  const downloadS3File = async (doc: CompanyDocumentRecord) => {
    const downloading = [...downloadingArray, doc.id];
    setDownloadingArray(downloading);
    await downloadFile(doc.name, doc.s3_key);
    const filtered = downloading.filter(item => item !== doc.id);
    setDownloadingArray(filtered);
  };

  const fetchSafeWorkMethod = async (data: { id: string }) => {
    try {
      const response = await getSafeWorkMethod(data.id);
      setSafeWorkMethod(response);
    } catch (e) {
      console.log("error == ", e);
    }
  };

  const handleTabChange = async (e: any) => {
    setActiveIndex(e.index);
    if (e.index === 2 && (!documents || documents.length === 0)) {
      fetchDocuments();
    }
  };

  const displaySafeWorkMethod = (data: SiteJobTaskRecord) => {
    return (
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <span>{data.safe_work_method}</span>

        <div
          style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          onClick={async () => {
            setSafeWorkMethodLoadingIndex(data.id);
            await fetchSafeWorkMethod({ id: JSON.stringify(data.safe_work_method_id) });
            setSafeWorkMethodLoadingIndex(null);
          }}
        >
          {safeWorkMethodLoadingIndex && safeWorkMethodLoadingIndex === data.id ? (
            <ProgressActivity className={"spinner"} />
          ) : (
            <Eye fill={"#007bff"} />
          )}
        </div>
      </div>
    );
  };

  const displayExpiredDate = (data: CompanyDocumentRecord) => {
    if (!data.expire_at) {
      return <StatusBadge variant={"info"}>No expiry date</StatusBadge>;
    }
    return (
      <div>
        {data.expired ? (
          <StatusBadge variant={"error"}>{formatDate(data.expire_at)}</StatusBadge>
        ) : (
          <StatusBadge variant={"info"}>{formatDate(data.expire_at)}</StatusBadge>
        )}
      </div>
    );
  };

  const displayDownloadButton = (data: CompanyDocumentRecord) => {
    if (!data.s3_key) {
      return (
        <>
          <DownloadOff className={"nodownload-icon"} />
        </>
      );
    }

    return (
      <div
        className={downloadingArray.includes(data.id) ? "downloading" : "download"}
        onClick={async () => {
          if (downloadingArray.includes(data.id)) {
            return;
          }
          await downloadS3File(data);
        }}
      >
        {downloadingArray.includes(data.id) ? (
          <ProgressActivity className={"spinner download-icon"} />
        ) : (
          <Download className={"download-icon"} />
        )}
      </div>
    );
  };

  const displayRequired = (data: CompanyDocumentRecord) => {
    const isValid = !data.expired && data.s3_key;

    return (
      <Fragment>
        {data.required ? (
          <>
            <StatusBadge variant={isValid ? "info" : "error"}>Required</StatusBadge>
          </>
        ) : (
          <StatusBadge variant={"success"}>Not Required</StatusBadge>
        )}
      </Fragment>
    );
  };

  return (
    <>
      <CardOverflow
        style={{ width: "100%" }}
        sx={{ borderTop: "1px solid", borderColor: "divider" }}
      >
        <CardActions
          style={{ marginTop: 15, marginBottom: 15 }}
          sx={{ alignSelf: "flex-end", pt: 2 }}
        >
          <Button
            name="button"
            icon="pi pi-save"
            label="Download PDF"
            className="p-button-outlined"
            size="small"
            disabled={true}
            style={{
              position: "absolute",
              fontSize: "small",
              right: 0,
              color: "#2dd36f",
            }}
            onClick={() => {}}
          />
        </CardActions>
      </CardOverflow>
      <TabView onTabChange={handleTabChange} activeIndex={activeIndex}>
        <TabPanel header={"Scope of Work"} key={"scope-of-work"}>
          {loadingSiteSummary ? (
            <div className={"loading-wrapper"}>
              <ProgressActivity className={"spinner"} />
            </div>
          ) : (
            <>
              {siteSummaryError ? (
                <div className={"error-wrapper"}>
                  <h2>Error fetching site summary</h2>
                </div>
              ) : (
                siteSummary && (
                  <div dangerouslySetInnerHTML={{ __html: siteSummary[0]?.scope_of_work }} />
                )
              )}
            </>
          )}
        </TabPanel>
        <TabPanel header={"Job tasks"} key={"job-tasks"}>
          {siteSummary && siteSummary[0].job_tasks && (
            <DataTable value={siteSummary[0]?.job_tasks}>
              <Column field={"name"} header={"Name"} />
              <Column field={"description"} header={"Description"} />
              <Column
                field={"safe_work_method"}
                header={"Safe Work Method"}
                body={displaySafeWorkMethod}
              />
            </DataTable>
          )}
        </TabPanel>
        <TabPanel header={"Documents"} key={"documents"}>
          {loadingSiteDocuments ? (
            <div className={"loading-wrapper"}>
              <ProgressActivity className={"spinner"} />
            </div>
          ) : (
            <>
              {siteDocumentsError ? (
                <div className={"error-wrapper"}>
                  <h2>Error fetching site documents</h2>
                </div>
              ) : (
                documents && (
                  <div>
                    <DataTable value={documents}>
                      <Column field={"name"} header={"Name"} />
                      <Column field={"description"} header={"Description"} />
                      <Column field={"status"} header={"Status"} />
                      <Column field={"expire_at"} header={"Expire at"} body={displayExpiredDate} />
                      <Column field={"required"} header={"Required"} body={displayRequired} />
                      <Column field={"download"} header={"Download"} body={displayDownloadButton} />
                    </DataTable>
                  </div>
                )
              )}
            </>
          )}
        </TabPanel>
      </TabView>
      {safeWorkMethod && (
        <Dialog
          onHide={() => {
            setSafeWorkMethod(null);
            // in the event that it is not null
            setSafeWorkMethodLoadingIndex(null);
          }}
          visible={!!safeWorkMethod}
          header={safeWorkMethod.name}
          style={{ width: "30vw" }}
        >
          <div dangerouslySetInnerHTML={{ __html: safeWorkMethod.description }} />
        </Dialog>
      )}
    </>
  );
}

export default SiteDocuments;
